import { Subject, Subscription } from "rxjs";
import type { Command } from "common/app/command-palette/types";

interface EventPayloadMap {
  RefreshPage: void;
  ChunkLoadingError: void;
  SchedulerReload: void;
  WorkOrderAssigned: string;
  AddPaletteCommands: Command[];
  RemovePaletteCommands: string[];
}

type EventType = keyof EventPayloadMap;

type Event<T extends EventType = EventType> = EventPayloadMap[T] extends void
  ? { type: T; payload?: void }
  : { type: T; payload: EventPayloadMap[T] };

class EventBus {
  private subjects: Partial<{
    [K in EventType]: Subject<EventPayloadMap[K]>;
  }> = {};

  private getSubject<T extends EventType>(
    eventType: T,
  ): Subject<EventPayloadMap[T]> {
    if (!this.subjects[eventType]) {
      this.subjects[eventType] = new Subject<
        EventPayloadMap[T]
      >() as Subject<any>;
    }
    return this.subjects[eventType] as Subject<EventPayloadMap[T]>;
  }

  /**
   * Emits an event on the bus.
   * @param event The event object, containing the type and optional payload.
   */
  emit = <T extends EventType>(event: Event<T>): void => {
    const subject = this.getSubject(event.type);
    subject.next(event.payload as EventPayloadMap[T]);
  };

  /**
   * Subscribes to an event type on the bus.
   * @param eventName The type of event to listen for.
   * @param callback The function to execute when the event is emitted.
   * It receives the payload associated with the event type.
   * @returns An RxJS Subscription object, which can be used to unsubscribe.
   */
  on = <T extends EventType>(
    eventName: T,
    callback: (payload: EventPayloadMap[T]) => void,
  ): Subscription => {
    const subject = this.getSubject(eventName);
    return subject.subscribe(callback);
  };

  dispose = (): void => {
    Object.values(this.subjects).forEach((subject) => {
      if (subject && !subject.closed) {
        subject.complete();
      }
    });
    this.subjects = {};
  };
}

export const eventBus = new EventBus();
